import { CONTACT_EMAIL, FAX, primaryPhone } from "@shared/contactDetails";
import {
  BarChart3,
  Building2,
  ClipboardList,
  Dumbbell,
  Handshake,
  Landmark,
  LineChart,
  ShieldCheck,
  UsersRound,
  Wrench,
  type LucideIcon,
} from "lucide-react";

export type SitePage = {
  slug: string;
  navLabel: string;
  eyebrow: string;
  title: string;
  description: string;
  metaTitle: string;
  metaDescription: string;
  icon: LucideIcon;
  highlights: { title: string; description: string }[];
  process?: { title: string; description: string }[];
  form?: "contact" | "blueprint" | "fitlife" | "client_application" | "coach_application" | "vendor_application";
  cta?: string;
};

export const colorLogo = "/media/altitude-logo-tight.png";
export const footerLogo = "/media/altitude-logo-footer-tight.png";
export const heroImage = "/media/altitude-hero.jpg";
export const personalTrainingHero = "/media/personal-training-hero.jpg";
export const consultingOperationsImage = "/media/altitude-consulting-operations.jpg";
export const managementServiceImage = "/media/altitude-service-management.jpg";
export const businessSolutionsImage = "/media/altitude-service-business-solutions.jpg";
export const facilityServiceImage = "/media/altitude-service-facility.jpg";
export const businessServiceImage = "/media/altitude-service-business.jpg";
export const financialServiceImage = "/media/altitude-service-financial.jpg";
export const caseStudyFacilityImage = "/media/project-a.jpg";
export const desertRoadVideo = "/media/altitude-desert-road-hero-web.mp4";

export const servicePageImages: Partial<Record<string, string>> = {
  consulting: consultingOperationsImage,
  management: managementServiceImage,
  "personal-training": personalTrainingHero,
  clients: personalTrainingHero,
  coaches: personalTrainingHero,
  "business-solutions": businessSolutionsImage,
  "facility-services": facilityServiceImage,
  "business-services": businessServiceImage,
  "financial-services": financialServiceImage,
  "management-services": managementServiceImage,
};

export const businessSolutionLinks = [
  { href: "/facility-services", label: "Facility Services" },
  { href: "/business-services", label: "Business Services" },
  { href: "/financial-services", label: "Financial Services" },
  { href: "/management-services", label: "Management Services" },
];

export const serviceLinks = [
  { href: "/consulting", label: "Consulting" },
  { href: "/management", label: "Management" },
  { href: "/personal-training", label: "Personal Training" },
  { href: "/business-solutions", label: "Business Solutions" },
];

export const resourceLinks = [
  { href: "/free-blueprint", label: "Free Blueprint" },
  { href: "/fitlife-x", label: "FitLife-X" },
  // The paid tier sits beside the free newsletter rather than replacing it;
  // /fitlife-x stays exactly what it was.
  { href: "/fitlife-x/pro", label: "FitLife-X Pro" },
  { href: "/partner-registration", label: "Partner Registration" },
  { href: "/clients/login", label: "Client Portal" },
];

/** The contact page's phone card. Falls back to the email address when no
 *  line is in service, so the card never advertises a number that does not
 *  ring. */
const contactCardPhone = (): string => {
  const phone = primaryPhone();
  return phone ? `${phone.label} · Fax ${FAX}` : `Email ${CONTACT_EMAIL} · Fax ${FAX}`;
};

export const pages: Record<string, SitePage> = {
  consulting: {
    slug: "consulting", navLabel: "Consulting", eyebrow: "Fitness business consulting", title: "Consulting that moves the needle.",
    description: "Our flagship advisory service diagnoses what is holding your fitness business back and creates a clear, fitness-specific plan to grow it—no generic playbooks, no fluff.",
    metaTitle: "Fitness Business Consulting for Gym Owners | Altitude", metaDescription: "Consulting built for gym and studio owners: fix retention, pricing, staffing and operations, then plan the next location. NYC based, working nationwide.", icon: LineChart,
    highlights: [
      { title: "Revenue & membership", description: "Pricing, offers, retention, and a marketing engine designed to grow members." },
      { title: "Operations & SOPs", description: "Standard operating procedures, staffing, and systems that scale without losing service." },
      { title: "Growth & expansion", description: "New locations, new concepts, and investor-ready planning for the next chapter." },
    ],
    process: [
      { title: "Diagnose", description: "Assess the business and isolate the biggest bottleneck." },
      { title: "Plan", description: "Build a tailored 12-month roadmap with clear priorities." },
      { title: "Build", description: "Implement alongside the team, not from a distance." },
      { title: "Scale", description: "Grow revenue, members, and locations with confidence." },
    ], form: "contact", cta: "Book a free consultation",
  },
  management: {
    slug: "management", navLabel: "Management", eyebrow: "Full-service management", title: "Full-service management.",
    description: "From day-to-day club operations to people, facilities, business, and finances, Altitude brings the operating discipline that keeps a fitness business moving forward.",
    metaTitle: "Gym & Health Club Management Company | Altitude", metaDescription: "A management company for gyms, studios and health clubs: daily operations, staffing and HR, facilities, revenue and reporting owners can act on.", icon: UsersRound,
    highlights: [
      { title: "Club operations", description: "Day-to-day management, service standards, and a member experience worth returning for." },
      { title: "People & HR", description: "Hiring, HR, staff training, performance, and retention systems." },
      { title: "Facilities", description: "Maintenance, vendor coordination, and facility operations that do not get in the way." },
      { title: "Business", description: "Revenue, marketing, and membership growth connected to the operating plan." },
      { title: "Financial", description: "Accounting, collections, and advisory in partnership with Northgate." },
      { title: "Strategy", description: "Planning and oversight across the entire operation." },
    ], form: "contact", cta: "Talk to us",
  },
  "personal-training": {
    slug: "personal-training", navLabel: "Personal Training", eyebrow: "Personal training", title: "A better personal training experience.",
    description: "Altitude creates a clear path for clients looking for the right trainer and coaches looking for the tools, opportunities, and support to grow.",
    metaTitle: "Personal Training Network, NYC | Altitude Fitness", metaDescription: "Get matched with a personal trainer in New York City, join the Altitude coach network, or build a personal training offer inside your facility.", icon: Dumbbell,
    highlights: [
      { title: "For clients", description: "Start with a consultation and evaluation, then get matched with a personal trainer who fits." },
      { title: "For coaches", description: "Apply to join the Altitude network and access new-client opportunities, CE, insurance, and tools." },
      { title: "For operators", description: "Build a coaching and personal-training offer that improves outcomes and strengthens revenue." },
    ], cta: "Explore personal training",
  },
  clients: {
    slug: "clients", navLabel: "Clients", eyebrow: "Personal training clients", title: "Find the right trainer, the right way.",
    description: "It begins with a consultation and evaluation. Then we match you with a personal trainer who fits your goals, style, and schedule.",
    metaTitle: "Find a Personal Trainer in NYC | Altitude Fitness", metaDescription: "Start with a consultation and movement assessment, then get matched with a New York City personal trainer who fits your goals, pace and schedule.", icon: Dumbbell,
    highlights: [
      { title: "Consult", description: "Tell us what you want to accomplish and what support would make the difference." },
      { title: "Evaluate", description: "Start with a grounded assessment that creates the right training baseline." },
      { title: "Match", description: "Get connected with a trainer whose approach fits your goals and pace." },
      { title: "Train", description: "Build the consistency and accountability that turn intent into results." },
    ], form: "client_application", cta: "Apply to work with us",
  },
  coaches: {
    slug: "coaches", navLabel: "Coaches & Trainers", eyebrow: "Altitude trainer network", title: "Join the Altitude network.",
    description: "Apply to work with Altitude, get matched with new clients, and access resources that help you grow—certifications, continuing education, trainer insurance, and more.",
    metaTitle: "Personal Trainer Jobs & Client Leads, NYC | Altitude", metaDescription: "Join the Altitude trainer network for new client opportunities in New York City, continuing education, insurance resources and business support.", icon: UsersRound,
    highlights: [
      { title: "New client opportunities", description: "A more reliable path to meeting people who are actively looking for the right trainer." },
      { title: "Certifications & CE", description: "Resources that support quality, credible professional development, and better outcomes." },
      { title: "Trainer insurance", description: "Insurance and liability resources designed for independent fitness professionals." },
    ], form: "coach_application", cta: "Apply to join",
  },
  "business-solutions": {
    slug: "business-solutions", navLabel: "Business Solutions", eyebrow: "Business solutions", title: "Business solutions for fitness operators.",
    description: "Four connected practice areas keep your business running and growing: facilities, business, financial, and management services—delivered directly or through vetted partners.",
    metaTitle: "Fitness Business Solutions for Operators | Altitude", metaDescription: "Facility build-out, business strategy, financial support and club management in one place, for independent gym and studio operators who want one partner.", icon: Building2,
    highlights: [
      { title: "Facility services", description: "Design, build-out, equipment, and maintenance." },
      { title: "Business services", description: "Strategy, operations, marketing, and growth." },
      { title: "Financial services", description: "Accounting, tax, collections, and advisory." },
      { title: "Management services", description: "Club management and people/HR systems." },
    ], form: "contact", cta: "Book a free consultation",
  },
  "facility-services": {
    slug: "facility-services", navLabel: "Facility Services", eyebrow: "Business solutions · facility", title: "Facilities that exceed expectations.",
    description: "From concept to construction to upkeep, we design, build, and maintain fitness facilities that look great and run efficiently.",
    metaTitle: "Gym Design & Build-Out Consultants | Altitude", metaDescription: "Gym and studio design, build-out management, equipment specification and maintenance \u2014 planned around how the facility will actually be operated.", icon: Building2,
    highlights: [
      { title: "Design", description: "Interior and exterior club design with professional drawings." },
      { title: "Build-out", description: "Top construction contractors and coordinated project management." },
      { title: "Equipment", description: "Outfitting by leading brands, sourced and installed with the whole facility in mind." },
      { title: "Maintenance", description: "Ongoing facility maintenance and operations support." },
    ], form: "contact", cta: "Talk to us",
  },
  "business-services": {
    slug: "business-services", navLabel: "Business Services", eyebrow: "Business solutions · strategy", title: "Strategy that drives growth.",
    description: "The business engine: revenue, marketing, membership, and the operating systems that make growth repeatable.",
    metaTitle: "Gym Marketing, Revenue & Operations Strategy | Altitude", metaDescription: "Membership pricing, local marketing, retention and documented operating procedures that make growth repeatable for gyms and boutique studios.", icon: BarChart3,
    highlights: [
      { title: "Revenue & membership", description: "Pricing, offers, acquisition, retention, and the right service mix." },
      { title: "Marketing", description: "A practical marketing strategy and online presence that moves prospects toward action." },
      { title: "Operations & SOPs", description: "Standard operating procedures and systems that make great service consistent." },
    ], form: "contact", cta: "Talk to us",
  },
  "financial-services": {
    slug: "financial-services", navLabel: "Financial Services", eyebrow: "Business solutions · financial", title: "Financial clarity & support.",
    description: "Accounting, tax, collections, and financial advisory delivered with our partner, Northgate Strategic Partners.",
    metaTitle: "Gym Accounting, Tax & Collections Support | Altitude", metaDescription: "Bookkeeping, monthly reporting, tax planning and receivables collections for fitness businesses, with Northgate Strategic Partners.", icon: Landmark,
    highlights: [
      { title: "Accounting", description: "Bookkeeping and financial reporting that makes the next decision clearer." },
      { title: "Tax", description: "Tax planning and preparation through Northgate Strategic Partners." },
      { title: "Collections", description: "Receivables and collections support that protects cash flow." },
      { title: "Advisory", description: "General business and financial advisory for the decisions in front of you." },
    ], form: "contact", cta: "Talk to us",
  },
  "management-services": {
    slug: "management-services", navLabel: "Management Services", eyebrow: "Business solutions · management", title: "Management & people, handled.",
    description: "A focused set of management offerings: reliable daily operations, plus the people and HR systems behind them.",
    metaTitle: "Gym Operations & HR Management Services | Altitude", metaDescription: "Day-to-day club operations, hiring and HR, staff training and performance oversight for gym owners who want the operation run to a standard.", icon: ShieldCheck,
    highlights: [
      { title: "Club operations", description: "Day-to-day management and the service standards that define your brand." },
      { title: "People & HR", description: "Hiring, HR management, staff training, and retention." },
      { title: "Oversight", description: "Performance systems and management support that build accountability." },
    ], form: "contact", cta: "Talk to us",
  },
  "free-blueprint": {
    slug: "free-blueprint", navLabel: "Free Blueprint", eyebrow: "Free business growth blueprint", title: "Your free Business Growth Blueprint.",
    description: "Tell us about your fitness business. Within 24 hours, we will send a personalized diagnostic that identifies your top opportunities, your biggest bottleneck, and a practical path forward.",
    metaTitle: "Free Gym Business Growth Blueprint | Altitude", metaDescription: "Answer nine questions about your gym and get a free Business Growth Blueprint back: your biggest constraint and three things to do about it this month.", icon: ClipboardList,
    highlights: [
      { title: "Top growth opportunities", description: "A sharper picture of the moves most likely to create momentum." },
      { title: "Single biggest bottleneck", description: "The constraint holding your next phase back, clearly identified." },
      { title: "12-month roadmap", description: "A focused direction for sequencing investments, systems, and effort." },
    ], form: "blueprint", cta: "Get my free blueprint",
  },
  "fitlife-x": {
    slug: "fitlife-x", navLabel: "FitLife-X", eyebrow: "FitLife-X", title: "The weekly on the business of fitness.",
    description: "Every week we share conversations on management, personal training, facility management and design, technology in fitness, and whatever is moving the industry.",
    metaTitle: "FitLife-X: Fitness Business Newsletter | Altitude", metaDescription: "A weekly briefing for gym and studio operators on management, retention, facilities and the decisions shaping the fitness industry.", icon: LineChart,
    highlights: [
      { title: "Management", description: "What smarter operating discipline looks like in real fitness businesses." },
      { title: "Training", description: "Insights for personal trainers, client outcomes, and stronger programs." },
      { title: "Facility & technology", description: "Design, equipment, systems, and emerging tools changing the industry." },
    ], form: "fitlife", cta: "Subscribe",
  },
  "partner-registration": {
    slug: "partner-registration", navLabel: "Partner Registration", eyebrow: "Altitude vendor network", title: "Become an Altitude vendor partner.",
    description: "Submit your company profile for review. Once approved, our team will follow up with the next steps for working together.",
    metaTitle: "Fitness Industry Vendor Network | Altitude", metaDescription: "Join Altitude's vendor network to reach gym and studio operators with equipment, construction, marketing and professional services.", icon: Handshake,
    highlights: [
      { title: "Share your profile", description: "Tell us what your company does and where it adds value for fitness operators." },
      { title: "Get reviewed", description: "Our team reviews vendor applications for fit, quality, and operator value." },
      { title: "Build better together", description: "Approved partners receive the next steps for engaging the Altitude network." },
    ], form: "vendor_application", cta: "Register & request vendor ID",
  },
  about: {
    slug: "about", navLabel: "About", eyebrow: "About Altitude", title: "Built for the business of fitness.",
    description: "Altitude Fitness Management Group is a New York City consulting and management firm helping fitness operators build, grow, and scale with more than 14 years of fitness-specific expertise.",
    metaTitle: "About Altitude Fitness Management Group | NYC", metaDescription: "A New York City fitness management and consulting firm working with independent gyms, studios and health clubs across the country.", icon: ShieldCheck,
    highlights: [
      { title: "Advisory", description: "Consulting, diagnosis, strategy, and roadmaps made for fitness operators." },
      { title: "Training", description: "Personal-training programs and a growing trainer network." },
      { title: "Business solutions", description: "Facility, business, financial, and management services, including People/HR." },
    ], form: "contact", cta: "Start a conversation",
  },
  contact: {
    slug: "contact", navLabel: "Contact", eyebrow: "Get in touch", title: "Let’s discuss your business.",
    description: "Whether you are launching, rebuilding, or scaling an established operation, we will help you determine the right next conversation.",
    metaTitle: "Talk to a Gym Management Consultant | Altitude", metaDescription: "Book a free fifteen-minute call with Altitude Fitness Management Group about managing, growing or building your gym, studio or health club.", icon: Handshake,
    highlights: [
      { title: "Office", description: "1185 Avenue of the Americas, Suite 303, New York, NY 10036" },
      { title: "Phone", description: contactCardPhone() },
      { title: "Email", description: "info@altitudefitlife.com" },
    ], form: "contact", cta: "Book a free consultation",
  },
};

export const homeMeta = {
  title: "Fitness Business Consulting & Management NYC | Altitude Fitness Management Group",
  description: "Altitude Fitness Management Group helps fitness operators launch, grow, and scale through consulting, management, personal training, and business solutions.",
};

export function pageForPath(path: string): SitePage | undefined {
  const slug = path.replace(/^\/+|\/+$/g, "") || "home";
  return pages[slug];
}
